import React from 'react';
import PropTypes from 'prop-types';

/**
 * GenerationSettingsSection - Question generation configuration
 */
function GenerationSettingsSection({
  numberOfQuestions,
  setNumberOfQuestions,
  difficulty,
  setDifficulty,
  includeCodeBlocks,
  setIncludeCodeBlocks,
  userGuidance,
  setUserGuidance,
  topicTags,
  currentTag,
  setCurrentTag,
  onAddTopicTag,
  onRemoveTopicTag,
  selectedSession,
  setSelectedSession,
  existingSessions
}) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
      <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">⚙️ Generierungs-Einstellungen</h2>
      
      <div className="space-y-4">
        {/* Number of Questions */}
        <div>
          <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Anzahl Fragen</label>
          <input
            type="number"
            min="1"
            max="50"
            value={numberOfQuestions}
            onChange={(e) => setNumberOfQuestions(parseInt(e.target.value))}
            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
        </div>

        {/* Difficulty */}
        <div>
          <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Schwierigkeit</label>
          <select
            value={difficulty}
            onChange={(e) => setDifficulty(e.target.value)}
            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="easy">Einfach - Grundwissen, einfache Konzepte</option>
            <option value="medium">Mittel - Detailwissen, Anwendung</option>
            <option value="hard">Schwer - Expertenwissen, komplexe Zusammenhänge</option>
            <option value="mixed">Gemischt - Verschiedene Schwierigkeitsgrade</option>
          </select>
        </div>

        {/* Include Code Blocks */}
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-gray-50 dark:bg-gray-700/50">
          <div className="flex items-start">
            <input
              type="checkbox"
              id="includeCode"
              checked={includeCodeBlocks}
              onChange={(e) => setIncludeCodeBlocks(e.target.checked)}
              className="mt-1 mr-3"
            />
            <div className="flex-1">
              <label htmlFor="includeCode" className="text-sm font-medium cursor-pointer text-gray-900 dark:text-white">
                💻 Code-Beispiele in Fragen einbeziehen
              </label>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                Wenn aktiviert: Fragen können Programmier-Code enthalten (z.B. &quot;Was gibt dieser Python-Code aus?&quot;)
                <br />
                Wenn deaktiviert: Nur textbasierte Fragen ohne Code-Snippets
              </p>
            </div>
          </div>
        </div>

        {/* User Guidance */}
        <div>
          <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Zusätzliche Anweisungen</label>
          <textarea
            value={userGuidance}
            onChange={(e) => setUserGuidance(e.target.value)}
            placeholder="z.B. 'Fokus auf Python-Grundlagen' oder 'Erstelle Fragen zu Datenstrukturen'"
            rows={3}
            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
          />
        </div>

        {/* Topic Tags */}
        <div>
          <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">🏷️ Themen-Tags (optional)</label>
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              value={currentTag}
              onChange={(e) => setCurrentTag(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onAddTopicTag();
                }
              }}
              placeholder="z.B. Python, Arrays, OOP"
              className="flex-1 p-2 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
            />
            <button
              onClick={onAddTopicTag}
              className="px-3 py-2 bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 rounded hover:bg-primary-200 dark:hover:bg-primary-800 text-sm"
            >
              + Tag
            </button>
          </div>
          {topicTags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {topicTags.map((tag, idx) => (
                <span
                  key={idx}
                  className="px-3 py-1 bg-secondary-100 dark:bg-secondary-900/50 text-secondary-700 dark:text-secondary-300 rounded-full text-sm flex items-center gap-1"
                >
                  {tag}
                  <button
                    onClick={() => onRemoveTopicTag(tag)}
                    className="text-secondary-500 dark:text-secondary-400 hover:text-secondary-800 dark:hover:text-secondary-200 font-bold"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Tags helfen dem LLM, fokussierte Fragen zu erstellen
          </p>
        </div>

        {/* Target Template */}
        <div>
          <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Ziel-Vorlage</label>
          <select
            value={selectedSession || ''}
            onChange={(e) => setSelectedSession(e.target.value)}
            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="">-- Vorlage auswählen --</option>
            {existingSessions.map(session => (
              <option key={session.id} value={session.id}>
                {session.name} ({session.questionCount} Fragen)
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}

GenerationSettingsSection.propTypes = {
  numberOfQuestions: PropTypes.number.isRequired,
  setNumberOfQuestions: PropTypes.func.isRequired,
  difficulty: PropTypes.string.isRequired,
  setDifficulty: PropTypes.func.isRequired,
  includeCodeBlocks: PropTypes.bool.isRequired,
  setIncludeCodeBlocks: PropTypes.func.isRequired,
  userGuidance: PropTypes.string.isRequired,
  setUserGuidance: PropTypes.func.isRequired,
  topicTags: PropTypes.array.isRequired,
  currentTag: PropTypes.string.isRequired,
  setCurrentTag: PropTypes.func.isRequired,
  onAddTopicTag: PropTypes.func.isRequired,
  onRemoveTopicTag: PropTypes.func.isRequired,
  selectedSession: PropTypes.string,
  setSelectedSession: PropTypes.func.isRequired,
  existingSessions: PropTypes.array.isRequired
};

export default GenerationSettingsSection;
